/**
 * damage_runtime — PERSISTENT MATERIAL DAMAGE, the game's half (shader gaps program round 2, 2026-09-29).
 *
 * The store (`damage_state.ts`, pure) keeps every hit in its receiver's LOCAL frame; the material layer
 * (`damage_layer.ts`) reads a uniform array of records. This file joins them to the world:
 *   • RECEIVERS — resolves a hit to a stable id + frame: a registered settlement building or player deed (owners call
 *     `registerDamageReceiver`), a structure solid with no owner (`solid:<x>:<z>:<base>`), a MOVER (the ride hull, a
 *     struck air target — `reportDamageHit({ mover })`), the ground's 64 m cell, else a 16 m static cell.
 *   • THE UPLOAD (RenderSub/9, after the rebase's `scene.position`): the static set (ground + buildings) nearest the
 *     camera resolved to RENDER space every frame (absolute − origin in doubles — no swim at a rebase), then each
 *     visible mover's records in its own frame. Heat is re-written every frame from the session clock.
 *   • SOURCES call `reportDamageHit` / `reportBlastDamage`; `applyDamageToScene` is the parse-time material hook.
 *   • LEVERS: `window.__damage()`, `__damageAt(x, y, z, kind, nx?, ny?, nz?)`, `__damageClear()`.
 *
 * Nothing here is authoritative and nothing crosses the wire: damage is presentation (the report: persist only where
 * gameplay requires it — nothing does).
 */
import * as THREE from 'three/webgpu';
import type { App } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { structureHitAt } from './structure_solids.js';
import { groundYIn } from './grounding.js';
import { groundNormal, structureFaceNormal, structureFacePoint } from './impact_decals.js';
import { emissiveNits } from './photometry.js';
import { biomeAt } from '@shared/terrain.js';
import {
  DamageState, frameFromYaw, frameFromMatrix, identityFrame, worldToLocal, groundCellId, staticCellId, solidReceiverId,
  surfaceForHit, KIND_SURFACE, SURFACES,
  type DamageKind, type SurfaceClass, type WeaponClass, type HitResult, type ReceiverType, type Frame,
} from './damage_state.js';
import {
  applyDamageLayer, damageStaticRange, damageMoverRanges, uDamageFull, uDamageHeat, damageLayerStats,
  uploadDamage, newUploadCache, markMoverMesh,
} from './damage_layer.js';
import { damageMode } from './damage_mode.js';
import { damageHooks } from './damage_hooks.js';

/** Static records the shader sees at once, by mode; the rest of the 64 slots are the movers'. */
export const STATIC_MAX: Record<'marks' | 'full', number> = { marks: 20, full: 32 };
/** Static records farther than this from the camera are not uploaded (a 1.7 m blast at 260 m is a few pixels). */
export const STATIC_RANGE_M = 260;
/** A mover is uploaded while its root is within this of the camera. */
export const MOVER_RANGE_M = 900;
/** The luminance of a white-hot core (cd/m²) — a lava-class emitter, on THE PHYSICAL-EMISSIVE CONTRACT's curve. */
export const DAMAGE_HEAT_NITS = 1200;
/** Registered-receiver search margin (m) around a building's box. */
const REG_MARGIN_M = 0.8;
const REG_CELL_M = 64;

const state = new DamageState();
export function damageState(): DamageState { return state; }

// ─── the session clock ─────────────────────────────────────────────────────
const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
export function damageNowS(): number { return ((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0) / 1000; }

// ─── registered static receivers (owners call these) ───────────────────────
interface RegRecv { id: string; frame: Frame; hx: number; hy: number; hz: number; surface: SurfaceClass; site: number; reach: number; cells: number[] }
const regById = new Map<string, RegRecv>();
const regBins = new Map<number, RegRecv[]>();
const cellKey = (cx: number, cz: number): number => (cx + 1_048_576) * 2_097_152 + (cz + 1_048_576);

/**
 * An owner registers a building as a damage receiver: `id` stable (a settlement placement, a structure id), the box
 * CENTRE (absolute) with three's yaw, the half-extents, the surface class, and the owner's `site` (to forget a whole
 * settlement at once). Registering the same id again moves it — its records RIDE (local coordinates).
 */
export function registerDamageReceiver(id: string, cx: number, cy: number, cz: number, yaw: number, hx: number, hy: number, hz: number, surface: SurfaceClass, site = 0): void {
  unbin(id);
  const frame = frameFromYaw(cx, cy, cz, yaw, 1);
  const reach = Math.hypot(hx, hz) + REG_MARGIN_M;
  const r: RegRecv = { id, frame, hx, hy, hz, surface, site, reach, cells: [] };
  const x0 = Math.floor((cx - reach) / REG_CELL_M), x1 = Math.floor((cx + reach) / REG_CELL_M);
  const z0 = Math.floor((cz - reach) / REG_CELL_M), z1 = Math.floor((cz + reach) / REG_CELL_M);
  for (let x = x0; x <= x1; x++) for (let z = z0; z <= z1; z++) {
    const k = cellKey(x, z);
    let arr = regBins.get(k);
    if (!arr) { arr = []; regBins.set(k, arr); }
    arr.push(r); r.cells.push(k);
  }
  regById.set(id, r);
  const have = state.receiver(id);
  if (have) { state.setFrame(id, frame); state.setLive(id, true); }
}
function unbin(id: string): void {
  const old = regById.get(id);
  if (!old) return;
  for (const k of old.cells) {
    const arr = regBins.get(k);
    if (!arr) continue;
    const i = arr.indexOf(old);
    if (i >= 0) arr.splice(i, 1);
    if (!arr.length) regBins.delete(k);
  }
  regById.delete(id);
}
/** An owner's building went away (streamed out): its records stay, keyed by id, and stop drawing. `drop` = demolished. */
export function forgetDamageReceiver(id: string, drop = false): void {
  unbin(id);
  state.setLive(id, false, drop);
}
/** A settlement streamed out: every building it registered stops drawing (the records wait for it to come back). */
export function forgetDamageSite(site: number): void {
  for (const r of [...regById.values()]) if (r.site === site) forgetDamageReceiver(r.id, false);
}
function registeredAt(x: number, y: number, z: number): RegRecv | null {
  const arr = regBins.get(cellKey(Math.floor(x / REG_CELL_M), Math.floor(z / REG_CELL_M)));
  if (!arr) return null;
  let best: RegRecv | null = null, bestV = Infinity;
  for (const r of arr) {
    const l = worldToLocal(r.frame, x, y, z);
    if (Math.abs(l[0]) > r.hx + REG_MARGIN_M || Math.abs(l[1]) > r.hy + REG_MARGIN_M || Math.abs(l[2]) > r.hz + REG_MARGIN_M) continue;
    const v = r.hx * r.hy * r.hz; // the smallest box containing the point (a kiosk in a courtyard, not the compound)
    if (v < bestV) { best = r; bestV = v; }
  }
  return best;
}

// ─── movers ────────────────────────────────────────────────────────────────
interface Mover { id: string; root: THREE.Object3D; surface: SurfaceClass }
const movers = new Map<string, Mover>();
/** Tag a mover's meshes so the draw reads ITS range and frame (never the static set). Idempotent; re-tags a rebuilt root. */
export function markDamageMover(id: string, root: THREE.Object3D, surface: SurfaceClass = 'coated_metal'): void {
  const had = movers.get(id);
  if (had && had.root === root) return;
  if (had && had.root !== root) had.root.traverse((o) => { if ((o.userData as Record<string, unknown>).damageRecv === id) delete (o.userData as Record<string, unknown>).damageRecv; });
  markMoverMesh(id, root);
  movers.set(id, { id, root, surface });
}

/** A mover's root was rebuilt (the ride re-made for the same hull): if it carries marks, the new root wears them. */
export function reattachDamageMover(id: string, root: THREE.Object3D): void {
  const r = state.receiver(id);
  if (r && r.records.length) markDamageMover(id, root, r.surface);
}

// ─── the scene + origin ────────────────────────────────────────────────────
let appRef: App | null = null;
const _v = new THREE.Vector3();
/** origin = −scene.position (the FP64 rebase: render = absolute − origin). */
function origin(): [number, number, number] {
  const sp = appRef?.world.scene.position;
  return sp ? [-sp.x, -sp.y, -sp.z] : [0, 0, 0];
}

let seedCache = '';
function commonsSeed(): string {
  if (seedCache) return seedCache;
  try {
    const conn = atlas.conn as unknown as { db?: { world?: { iter(): Iterable<{ kind: string; terrainSeed: string }> } } } | null;
    for (const w of conn?.db?.world?.iter() ?? []) if (w.kind === 'commons') { seedCache = w.terrainSeed; break; }
  } catch { /* rows mid-churn */ }
  return seedCache;
}

// ─── reporting ─────────────────────────────────────────────────────────────
export interface DamageHit {
  /** ABSOLUTE world point + the outward surface normal. */
  x: number; y: number; z: number; nx: number; ny: number; nz: number;
  weapon: WeaponClass;
  /** PhysicsModule's collider class, or 'ground' from the analytic walk; the biome at the hit (for ground). */
  collider?: string | null;
  biome?: string | null;
  /** Radius scale (the weapon's punch × an oblique stretch) and energy scale. */
  punch?: number;
  energy?: number;
  /** A moving receiver (the ride hull, an air target): the marks ride its root. */
  mover?: { id: string; root: THREE.Object3D; surface?: SurfaceClass } | null;
  /** Force a surface class (the probe lever). */
  surface?: SurfaceClass;
}

/** Record one hit. Null when damage is off or nothing could be resolved. */
export function reportDamageHit(h: DamageHit): HitResult | null {
  if (damageMode() === 'off') return null;
  if (![h.x, h.y, h.z, h.nx, h.ny, h.nz].every(Number.isFinite)) return null;
  const t = damageNowS();
  let id: string, type: ReceiverType, surface: SurfaceClass, frame: Frame;
  if (h.mover) {
    const root = h.mover.root;
    root.updateWorldMatrix(true, false);
    const o = origin();
    frame = frameFromMatrix(root.matrixWorld.elements, o[0], o[1], o[2]);
    id = h.mover.id; type = 'mover'; surface = h.surface ?? h.mover.surface ?? 'coated_metal';
    markDamageMover(id, root, surface);
  } else if (h.collider === 'terrain' || h.collider === 'ground') {
    const c = groundCellId(h.x, h.z);
    id = c.id; type = 'ground'; frame = identityFrame(c.cx * 64, 0, c.cz * 64);
    surface = h.surface ?? surfaceForHit(h.collider, h.biome ?? null);
  } else {
    const reg = registeredAt(h.x, h.y, h.z);
    if (reg) { id = reg.id; type = 'static'; frame = reg.frame; surface = h.surface ?? reg.surface; }
    else {
      const s = h.collider === 'structure' ? structureHitAt(h.x, h.z, h.y, REG_MARGIN_M) : null;
      if (s) { id = solidReceiverId(s); type = 'static'; frame = frameFromYaw(s.x, s.base, s.z, s.yaw, 1); surface = h.surface ?? 'coated_metal'; }
      else { const c = staticCellId(h.x, h.y, h.z); id = c.id; type = 'static'; frame = identityFrame(c.cx * 16, c.cy * 16, c.cz * 16); surface = h.surface ?? surfaceForHit(h.collider ?? null, h.biome ?? null); }
    }
  }
  const res = state.addHit({ id, type, surface, weapon: h.weapon, frame, x: h.x, y: h.y, z: h.z, nx: h.nx, ny: h.ny, nz: h.nz, punch: h.punch, energy: h.energy, t });
  runtimeStats.reported++;
  return res;
}

/** A RENDER-space point (a raycaster's hit) → ABSOLUTE (+ origin), in place. */
export function renderToAbsolute(v: THREE.Vector3): THREE.Vector3 {
  const o = origin();
  return v.set(v.x + o[0], v.y + o[1], v.z + o[2]);
}

/** A hit on the analytic ground at an ABSOLUTE point: the biome decides the surface (the seed's, like surface_response). */
export function reportGroundHit(x: number, y: number, z: number, nx: number, ny: number, nz: number, weapon: WeaponClass, punch = 1): HitResult | null {
  const seed = commonsSeed();
  let biome: string | null = null;
  try { biome = seed ? biomeAt(seed, x, z) : null; } catch { biome = null; }
  return reportDamageHit({ x, y, z, nx, ny, nz, weapon, collider: 'ground', biome, punch });
}

/**
 * THE DECAL YIELDS TO THE MATERIAL: on a surface whose own material shows the damage (a building, a hull) the fresh
 * soot quad fades in seconds so the stripped metal / the char shows through; elsewhere the decal keeps its life.
 * undefined = the decal's own life.
 */
export const DAMAGE_DECAL_LIFE_S = 9;
export function damageDecalLifeS(collider: string | null | undefined, at?: { x: number; y: number; z: number } | null): number | undefined {
  if (damageMode() === 'off' || collider !== 'structure') return undefined;
  // only where a BUILDING's own material shows the damage — a tree trunk or a rock is 'structure' to the physics too,
  // and its scorch must keep its whole life (foliage wears no damage layer)
  if (at && !registeredAt(at.x, at.y, at.z) && !structureHitAt(at.x, at.z, at.y, REG_MARGIN_M)) return undefined;
  return DAMAGE_DECAL_LIFE_S;
}

/**
 * A staged explosion at an ABSOLUTE point (`explosion_fx`, the cannon, a crash): a gouge / char in the ground under it
 * when it went off near the ground, and a strip on the face of a building it went off beside.
 */
export function reportBlastDamage(x: number, y: number, z: number, scale: number): void {
  if (damageMode() === 'off' || ![x, y, z, scale].every(Number.isFinite)) return;
  const s = Math.max(0.4, scale);
  const conn = atlas.conn;
  if (conn) {
    const wid = atlas.currentWorldId;
    const gy = groundYIn(conn, wid, x, z);
    if (Number.isFinite(gy) && y - gy <= 2.5 * s + 1) {
      const n = groundNormal((px, pz) => groundYIn(conn, wid, px, pz), x, z);
      reportGroundHit(x, gy, z, n.x, n.y, n.z, 'blast', Math.min(3, s));
    }
  }
  const sol = structureHitAt(x, z, y, 2 * s);
  if (sol) {
    const fp = structureFacePoint(sol, x, y, z), fn = structureFaceNormal(sol, x, y, z);
    reportDamageHit({ x: fp.x, y: fp.y, z: fp.z, nx: fn.x, ny: fn.y, nz: fn.z, weapon: 'blast', collider: 'structure', punch: Math.min(2.5, s * 0.8) });
  }
}

// ─── the parse-time hook ───────────────────────────────────────────────────
const DAMAGE_KINDS = new Set(['structure', 'prop', 'decor']);
/** Compose the layer onto every opaque standard material of a freshly parsed asset (structures, props, decor). */
export function applyDamageToScene(scene: THREE.Object3D, kind: string): number {
  const mode = damageMode();
  if (mode === 'off' || !DAMAGE_KINDS.has(kind)) return 0;
  let n = 0;
  scene.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.material) return;
    for (const mat of Array.isArray(m.material) ? m.material : [m.material]) {
      if ((mat.userData as Record<string, unknown>).interior) continue; // interior-mapped glass is its own shader
      try { if (applyDamageLayer(mat, { mode })) n++; } catch (e) { runtimeStats.layerErrors++; runtimeStats.lastError = String(e).slice(0, 160); }
    }
  });
  return n;
}

// ─── the per-frame upload ──────────────────────────────────────────────────
export const runtimeStats = { reported: 0, uploads: 0, staticUploaded: 0, moversUploaded: 0, layerErrors: 0, lastError: '' };
const uploadCache = newUploadCache();
let lastWorld: unknown = undefined;

function frameUpload(): void {
  const mode = damageMode();
  (uDamageFull as unknown as { value: number }).value = mode === 'full' && !atlas.xrPresenting ? 1 : 0;
  if (mode === 'off' || !appRef) {
    damageStaticRange.count = 0;
    for (const b of damageMoverRanges.values()) b.count = 0;
    runtimeStats.staticUploaded = 0; runtimeStats.moversUploaded = 0;
    return;
  }
  if (atlas.currentWorldId !== lastWorld) { if (lastWorld !== undefined) state.clear(); lastWorld = atlas.currentWorldId; seedCache = ''; }
  (uDamageHeat as unknown as { value: number }).value = emissiveNits(DAMAGE_HEAT_NITS);
  const o = origin();
  let cam: THREE.Camera | null = null;
  try { cam = appRef.renderer.cameras.main as unknown as THREE.Camera; } catch { cam = null; }
  if (!cam) return;
  cam.getWorldPosition(_v);
  // In the space sector the camera is in the SECTOR's frame: a Commons record near the same numbers must never paint a
  // ship — no static set there (movers keep their own frames).
  const up = uploadDamage(state, uploadCache, {
    cam: [_v.x + o[0], _v.y + o[1], _v.z + o[2]], origin: o, now: damageNowS(),
    staticMax: atlas.spaceFlight ? 0 : STATIC_MAX[mode], staticRange: STATIC_RANGE_M, movers: movers.values(), moverRange: MOVER_RANGE_M,
  });
  runtimeStats.uploads++; runtimeStats.staticUploaded = up.statics; runtimeStats.moversUploaded = up.movers;
}

const _ray = new THREE.Raycaster();
function coverAt(x: number, y: number, z: number, n: number) {
  let cam: THREE.Camera | null = null;
  try { cam = appRef?.renderer.cameras.main as unknown as THREE.Camera; } catch { cam = null; }
  if (!cam || !appRef) return null;
  const scene = appRef.world.scene as unknown as THREE.Object3D;
  const eye = cam.getWorldPosition(new THREE.Vector3());
  // absolute → render: + scene.position (the rebase law)
  const target = new THREE.Vector3(x + scene.position.x, y + scene.position.y, z + scene.position.z);
  const dist = eye.distanceTo(target);
  _ray.set(eye, target.clone().sub(eye).normalize()); _ray.far = dist + 5;
  _ray.camera = cam; // three's Sprite.raycast reads raycaster.camera
  const hits: THREE.Intersection[] = [];
  for (const child of scene.children) { try { _ray.intersectObject(child, true, hits); } catch { /* an object three cannot raycast */ } }
  const shown = (o: THREE.Object3D | null): boolean => { for (let p = o; p; p = p.parent) if (!p.visible) return false; return true; };
  const seen = hits.filter((h) => (h.object as THREE.Mesh).isMesh && shown(h.object)).sort((a, b) => a.distance - b.distance);
  return { targetDist: +dist.toFixed(2), hits: seen.slice(0, n).map((h) => {
    const mat = (Array.isArray((h.object as THREE.Mesh).material) ? ((h.object as THREE.Mesh).material as THREE.Material[])[0] : (h.object as THREE.Mesh).material) as THREE.Material | undefined;
    return { object: h.object.name || h.object.type, material: mat?.name || mat?.type || '', layer: (mat?.userData as Record<string, unknown> | undefined)?.damageLayer ?? null, dist: +h.distance.toFixed(2) };
  }) };
}

// ─── install ───────────────────────────────────────────────────────────────
let installed = false;
export function installDamageRuntime(app: App): void {
  if (installed) return;
  installed = true;
  appRef = app;
  damageHooks.blast = reportBlastDamage;
  app.world.addSystem(makeSystem('AtlasDamageUpload', Phase.RenderSub, 9, () => { try { frameUpload(); } catch (e) { runtimeStats.lastError = String(e).slice(0, 160); } }));
  bus.on('atlas:world-entered', ({ worldId }) => { if (worldId !== lastWorld && lastWorld !== undefined) state.clear(); lastWorld = worldId; seedCache = ''; });
  const w = window as unknown as Record<string, unknown>;
  w.__damage = () => ({
    mode: damageMode(), receivers: state.receivers.size, records: state.records, bytes: state.bytes, cap: state.bytesCap,
    staticUploaded: runtimeStats.staticUploaded, moversUploaded: runtimeStats.moversUploaded, registered: regById.size, selections: uploadCache.selections,
    movers: [...movers.keys()], last: state.last, stats: { ...state.stats }, runtime: { ...runtimeStats }, layer: { ...damageLayerStats },
    /** The registered buildings nearest the player: id, box centre (absolute), yaw, half-extents, distance. */
    registeredNear: (n = 5) => [...regById.values()].map((r) => ({ id: r.id, cx: r.frame.px, cy: r.frame.py, cz: r.frame.pz, yaw: 2 * Math.atan2(r.frame.qy, r.frame.qw), hx: r.hx, hy: r.hy, hz: r.hz, surface: r.surface, d: Math.hypot(r.frame.px - atlas.localX, r.frame.pz - atlas.localZ) }))
      .sort((a, b) => a.d - b.d).slice(0, n),
    list: (n = 20) => [...state.receivers.values()].sort((a, b) => b.lastHitT - a.lastHitT).slice(0, n)
      .map((r) => ({ id: r.id, type: r.type, surface: r.surface, live: r.live, records: r.records.length, kinds: [...new Set(r.records.map((x) => x.kind))] })),
    /** What the camera sees first on its way to an ABSOLUTE point (probe diagnostic): the nearest hits with their
     *  material and whether it wears the layer — a mark under a road ribbon or a plaza never shows, however right it is. */
    cover: (x: number, y: number, z: number, n = 4) => coverAt(x, y, z, n),
  });
  w.__damageAt = (x: number, y: number, z: number, kind: DamageKind = 'strip', nx = 0, ny = 1, nz = 0, weapon: WeaponClass = 'energy', punch = 1) => {
    const surface = KIND_SURFACE[kind] ?? (SURFACES.includes(kind as unknown as SurfaceClass) ? kind as unknown as SurfaceClass : 'coated_metal');
    const onGround = surface === 'soil' || surface === 'sand' || surface === 'rock' || surface === 'crystal';
    const r = reportDamageHit({ x, y, z, nx, ny, nz, weapon, punch, surface, collider: onGround && ny > 0.7 ? 'ground' : 'structure' });
    return r ? { receiver: r.receiver.id, local: r.local.map((v) => +v.toFixed(3)), merged: r.merged, kind: r.record.kind } : null;
  };
  w.__damageClear = () => { state.clear(); };
}
